/**
 * weeklyline.ts -- the current week's XI/XII reversal verdict as ONE line of text, or nothing.
 *
 * Andrei's constraints are hard requirements and they are the whole design: no table
 * ("I don't like this table, which I don't know how to use"), no new pane ("this indicator
 * should not overwhelm my UI panes even more"), current week only -- and since 2026-08-24,
 * NOTHING ON THE CHART AT ALL and nothing printed unless a profile is LIVE. He asked for the
 * feature to be deleted; the measurement said ten of the twelve profiles deserved that and two
 * did not, so what is left is one row of text in existing chrome and no geometry.
 *
 * WHAT WAS DELETED HERE, each item for a measured reason
 * (`docs/research/WEEKLY_PROFILE_BACKTEST_2026-08-19.md`):
 *
 *   * `weeklyRays` / `weeklyRayFrom` / `WeeklyRay` -- the labelled gold dashed rays. The payload
 *     no longer carries prices at all, so there is nothing to draw and no channel through which
 *     a bar index can be drawn as a price (that was R4-1, critical, and it is retired by removing
 *     the channel rather than by filtering it).
 *   * `weeklySetRows` / `daySpark` -- the surviving/eliminated expand. It listed the ten refuted
 *     profiles; with them gone the "set still consistent with what printed" is one profile, which
 *     the line itself says.
 *   * The `forming` and `standdown` lines. A forming week now prints NOTHING: it is ~3 weeks in 4
 *     and Andrei's complaint was clutter. The stand-down came from IX/X, whose only measurement
 *     (+0.584R in those weeks against +0.004R elsewhere, n=26) pointed the OPPOSITE way to the
 *     instruction it printed -- exactly the I-IV error, so the profiles and the line went together.
 *
 * THE FOUR RULES THAT SURVIVE, and they are what the two remaining profiles are allowed to say:
 *
 *   1. A PROBABILITY IS NEVER SHOWN WITHOUT ITS BASELINE. XII pooled is 84.0% against a
 *      same-weekday baseline of 66.2%: "84%" alone claims certainty. Both numbers and the lift,
 *      or none of them. `confidenceClause` is the only function that may print either.
 *   2. NO TP. XII's target was reached in 0.0% of 262 fires and XI's in 19.7%, so the caveat rides
 *      on the line itself rather than in a hover a reader may not open -- a direction call whose
 *      destination is unmeasured is how a stop gets placed against a level nothing reaches.
 *   3. `insufficient_data` AND `not_measured_weekday` RENDER AS WORDS, never as a number. The walk
 *      measured Tue-Fri only, and the pooled Tue-Fri number served to a Saturday decision read
 *      +11.9pp for XI where the true same-day lift is -5.5pp.
 *   4. ICT's corpus figure may never appear alone. It is not printed at all now -- neither XI nor
 *      XII carries one in the corpus -- and `priorClause` is deleted with the prior itself: the
 *      70% belonged to I and II, the two profiles the walk refuted hardest.
 *
 * THE PROVENANCE IS REACHABLE, NOT PRINTED. The expected path and the claim measured live in the
 * row's `title`, so the line stays one line.
 */

/** `/api/weekly/{asset}/{tf}` -- the fields this line reads. See `chartapp/api/weekly.py`. */
export type WeeklyPayload = {
  week_start: string;
  forming: boolean;
  bias: string;
  profile: {
    numeral: string;
    name: string;
    direction: string;
    member: string | null;
    path: string[];
    met: string[];
  } | null;
  calibration: {
    p: number | null;
    baseline_p: number | null;
    lift_pp: number | null;
    n: number | null;
    weekday: number | null;
    insufficient_data: boolean;
    /** Set when the walk never evaluated this decision weekday at all (Mon/Sat/Sun). */
    not_measured_weekday?: string | null;
    claim: string | null;
  } | null;
  /** The measured no-TP caveat, from the backtest. Carried, never composed here. */
  no_target: string;
  notes: string[];
};

export type WeeklyLine = {
  /** The one line. */
  text: string;
  /** Hover text: the provenance and the expected path. Always non-empty. */
  title: string;
};

/**
 * Timeframes on which `/api/weekly` REFUSES by design (`chartapp/api/weekly.py::_TOO_COARSE`):
 * one bar IS the week, so there is no partially-formed week to classify.
 *
 * A refusal the client can PREDICT is not fetched: `loadWeekly` used to ask the server to say no
 * on every 1W and 1M pane load, and the resulting 503 took `PostDeploySmokeDev`'s "the live app
 * loads with no console error" RED against a deployment where nothing was wrong. NOW IT PRINTS
 * NOTHING EITHER WAY -- a coarse pane is silent, like a forming week -- so this is a list of
 * timeframes not to FETCH rather than a sentence to render.
 */
const TOO_COARSE = ['1W', '1M'];

/** True when this pane's timeframe cannot answer, so no request is made and no row is drawn. */
export function weeklyRefused(tf: string): boolean {
  return TOO_COARSE.includes(tf);
}

const pct = (v: number): string => `${(v * 100).toFixed(1)}%`;

/**
 * The confidence clause, or the reason there isn't one.
 *
 * ONE FUNCTION, so `p` cannot reach the screen without `baseline_p` by any path -- the
 * guard is structural rather than a rule someone has to remember at each call site.
 */
export function confidenceClause(c: WeeklyPayload['calibration']): string {
  if (c === null) return 'not calibrated';
  // NEVER MEASURED ON THIS DAY is its own sentence (redteam D4). The walk covered Tue-Fri only; the
  // pooled Tue-Fri number used to be served to a Saturday decision as +11.9pp where the true
  // same-day lift is -5.5pp, and to a Sunday against a 100.0% baseline. "Too thin" and "not looked
  // at" are different facts and a reader must be able to tell them apart.
  if (c.not_measured_weekday) return `not measured on ${c.not_measured_weekday}`;
  if (c.insufficient_data || c.p === null || c.baseline_p === null) {
    return `insufficient data${c.n === null ? '' : ` (n=${c.n})`}`;
  }
  const lift = c.lift_pp ?? (c.p - c.baseline_p) * 100;
  const sign = lift >= 0 ? '+' : '';
  // WORSE THAN NOTHING, said in words. Neither surviving profile is in that state (XI +11.9pp,
  // XII +17.8pp pooled, and every weekday cell of both is positive), but a per-weekday cell is
  // read at runtime and this is the sentence the backtest wrote for a negative one -- keeping it
  // is what stops a future thin cell reading as an edge, which is the whole I-IV error.
  const verdict = lift < 0 ? ' WORSE THAN BASELINE' : '';
  return `${pct(c.p)} vs ${pct(c.baseline_p)} base${verdict} (${sign}${lift.toFixed(1)}pp, n=${c.n})`;
}

/** The hover text. Everything that does not fit on one line, and nothing invented. */
export function weeklyTitle(w: WeeklyPayload): string {
  const out: string[] = [`week of ${w.week_start}; daily bias ${w.bias}`];
  if (w.profile) {
    out.push(`${w.profile.numeral} ${w.profile.name}`);
    if (w.profile.member) out.push(`array member ${w.profile.member}`);
    // The PROJECTED PATH is prose, not geometry: `ProfileEval.path` is three sentences
    // describing consolidation -> raid -> reversal. It is shown here rather than drawn
    // because the detector emits no prices to draw it through -- see `no_target`.
    if (w.profile.path.length) out.push(`expected path: ${w.profile.path.join(' -> ')}`);
  }
  if (w.calibration?.claim) out.push(`claim measured: ${w.calibration.claim}`);
  out.push(w.no_target);
  out.push(...w.notes);
  return out.join('\n');
}

/**
 * The line itself, or `null` for NO ROW AT ALL.
 *
 * NOTHING IS PRINTED UNTIL A PROFILE IS LIVE, which is the card's own requirement and a change
 * from the four-state version (forming / profile / standdown / undecidable). A forming week is
 * ~3 weeks in 4, so a "week forming" row was permanent furniture saying nothing -- and permanent
 * furniture is what Andrei asked to have removed.
 *
 * THE NO-TP CAVEAT IS ON THE LINE, NOT IN THE HOVER. It is the one thing about these two profiles
 * that can cost money if it is not read: they call a direction, and the measured target was
 * reached in 0.0% (XII, n=262) and 19.7% (XI, n=249) of fires.
 */
export function weeklyLine(w: WeeklyPayload): WeeklyLine | null {
  if (w.forming || w.profile === null) return null;
  const p = w.profile;
  const dir = p.direction === 'bullish' ? 'BULLISH' : 'BEARISH';
  // ORDER IS A MEASUREMENT (card t_9f1ca4a9, re-applied here). The row CLIPS with a CSS ellipsis,
  // and at three 500px panes the name+direction prefix cost 367 of the 560px the clause needed --
  // so with the name first, the part that must not be lost was the part off-screen. Priority:
  // numeral, direction, the measured numbers, the no-TP warning, and the NAME LAST where the
  // ellipsis lands (the hover carries it in full).
  return {
    text: `week ${p.numeral} \u00b7 ${dir} \u00b7 ${confidenceClause(w.calibration)}`
      + ` \u00b7 NO TP \u00b7 ${p.name}`,
    title: weeklyTitle(w),
  };
}
